/**
 * Schedule & Incremental State API — CRUD, toggle, trigger, run history.
 */
import { apiClient, buildUrl } from './apiClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Schedule {
  schedule_id: string;
  project_id: string;
  tenant_id: string;
  name: string;
  cron_expression: string;
  cron_description?: string;
  is_enabled: boolean;
  load_mode: string;
  created_by?: string;
  next_run_at?: string;
  last_run_at?: string;
  last_run_status?: string;
  consecutive_failures: number;
  max_failures: number;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ScheduleRun {
  run_id: string;
  schedule_id: string;
  batch_id?: string;
  execution_run_id?: string;
  status: string;
  phase?: string;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  metadata?: Record<string, unknown>;
  created_at: string;
}

export interface IncrementalState {
  state_id: string;
  project_id: string;
  connector_id: string;
  table_name: string;
  watermark_column?: string;
  watermark_value?: string;
  watermark_type: string;
  rows_loaded: number;
  files_processed: string[];
  last_success_at?: string;
  created_at: string;
  updated_at: string;
}

export interface PipelineRunLog {
  log_id: string;
  run_id: string;
  phase: string;
  severity: 'info' | 'warn' | 'error';
  message: string;
  metrics: Record<string, any>;
  created_at: string;
}

export interface ScheduleCreateRequest {
  project_id: string;
  name: string;
  cron_expression: string;
  load_mode?: string;
  source_batch_id?: string;
  source_connector_id?: string;
  staging_mode?: string;
  metadata?: Record<string, any>;
}

export interface ScheduleUpdateRequest {
  name?: string;
  cron_expression?: string;
  load_mode?: string;
  max_failures?: number;
  metadata?: Record<string, any>;
}

// ---------------------------------------------------------------------------
// Schedule CRUD
// ---------------------------------------------------------------------------

export async function createSchedule(payload: ScheduleCreateRequest): Promise<{ schedule_id: string }> {
  const response = await apiClient.post<{ schedule_id: string }>(
    buildUrl('/api/v1/schedules'),
    payload,
  );
  return response.data;
}

export async function listSchedules(projectId?: string): Promise<Schedule[]> {
  const params = projectId ? `?project_id=${projectId}` : '';
  const response = await apiClient.get<{ schedules: Schedule[] }>(
    buildUrl(`/api/v1/schedules${params}`),
  );
  return response.data?.schedules ?? [];
}

export async function getSchedule(scheduleId: string): Promise<Schedule> {
  const response = await apiClient.get<Schedule>(
    buildUrl(`/api/v1/schedules/${scheduleId}`),
  );
  return response.data;
}

export async function updateSchedule(
  scheduleId: string,
  payload: ScheduleUpdateRequest,
): Promise<void> {
  await apiClient.put(buildUrl(`/api/v1/schedules/${scheduleId}`), payload);
}

export async function deleteSchedule(scheduleId: string): Promise<void> {
  await apiClient.delete(buildUrl(`/api/v1/schedules/${scheduleId}`));
}

export async function toggleSchedule(
  scheduleId: string,
  isEnabled: boolean,
): Promise<void> {
  await apiClient.post(buildUrl(`/api/v1/schedules/${scheduleId}/toggle`), {
    is_enabled: isEnabled,
  });
}

export async function triggerSchedule(scheduleId: string): Promise<void> {
  await apiClient.post(buildUrl(`/api/v1/schedules/${scheduleId}/trigger`));
}

export async function listScheduleRuns(
  scheduleId: string,
  limit = 20,
): Promise<ScheduleRun[]> {
  const response = await apiClient.get<{ runs: ScheduleRun[] }>(
    buildUrl(`/api/v1/schedules/${scheduleId}/runs?limit=${limit}`),
  );
  return response.data?.runs ?? [];
}

export async function listRunLogs(
  scheduleId: string,
  runId: string,
  limit = 200,
): Promise<PipelineRunLog[]> {
  const response = await apiClient.get<{ logs: PipelineRunLog[] }>(
    buildUrl(`/api/v1/schedules/${scheduleId}/runs/${runId}/logs?limit=${limit}`),
  );
  return response.data?.logs ?? [];
}

export async function approveScheduleRun(
  scheduleId: string,
  runId: string,
): Promise<void> {
  await apiClient.post(
    buildUrl(`/api/v1/schedules/${scheduleId}/runs/${runId}/approve`),
  );
}

// ---------------------------------------------------------------------------
// Incremental State
// ---------------------------------------------------------------------------

export async function listIncrementalStates(
  projectId: string,
  connectorId?: string,
): Promise<IncrementalState[]> {
  const params = connectorId ? `?connector_id=${connectorId}` : '';
  const response = await apiClient.get<{ states: IncrementalState[] }>(
    buildUrl(`/api/v1/projects/${projectId}/incremental-state${params}`),
  );
  return response.data?.states ?? [];
}

export async function resetIncrementalState(
  projectId: string,
  stateId: string,
): Promise<void> {
  await apiClient.post(
    buildUrl(`/api/v1/projects/${projectId}/incremental-state/${stateId}/reset`),
  );
}

